import React, {
  FunctionComponent,
  useState,
  useRef,
  useEffect,
  ChangeEvent,
} from "react";
import { EnrollmentFormProps } from "./../EnrollmentFormPage";
import BackNextButtons from "./../BackNextButtons";
import { FocusableHeader, DOBValidator } from "../../../components";
import {
  useTranslate,
  getValidationObject,
  EnrollmentPages,
} from "../../../helpers";
import PlanInfo from "./../PlanInfo";
import {
  Alert,
  ChoiceList,
  Dropdown,
  TextField,
} from "@cmsgov/ds-medicare-gov";
import PhoneNumberInput from "./../PhoneNumberInput";
import { Title, Sex, MCTDate } from "../../../@types";
import { removeExtraWhiteSpace } from "../../../helpers/enrollmentFormHelpers";

const PersonalInfo: FunctionComponent<EnrollmentFormProps> = ({
  enrollData,
  setEnrollData,
  previousPage,
  nextPage,
  plan,
  inReview,
}) => {
  const t = useTranslate();
  const [validForm, setValidForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const alertDiv = useRef<HTMLDivElement>(null);

  const [sexError, setSexError] = useState<string | undefined>(undefined);
  const [sexTouched, setSexTouched] = useState(false);
  const [firstNameError, setFirstNameError] = useState<string | undefined>(
    undefined
  );
  const [firstNameTouched, setFirstNameTouched] = useState(false);
  const [lastNameError, setLastNameError] = useState<string | undefined>(
    undefined
  );
  const [middleInitialError, setMiddleInitialError] = useState<
    string | undefined
  >(undefined);
  const [middleInitialTouched, setMiddleInitialTouched] = useState(false);
  const [lastNameTouched, setLastNameTouched] = useState(false);
  const [dobTouched, setDobTouched] = useState(false);
  const [dobError, setDobError] = useState<string | undefined>(undefined);
  const [phoneError, setPhoneError] = useState<string | undefined>(undefined);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [titleError, setTitleError] = useState<string | undefined>(undefined);
  const [titleTouched, setTitleTouched] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);

  const validateForm = (): void => {
    const formStatus = getValidationObject(
      enrollData,
      plan,
      EnrollmentPages.PersonalInfo
    );

    if (!formStatus.titleValidity && titleTouched) {
      setTitleError(t("enroll_form.choose_title"));
    } else {
      setTitleError(undefined);
    }

    if (!formStatus.firstNameValidity && firstNameTouched) {
      setFirstNameError(t("enroll_form.enter_first_name"));
    } else {
      setFirstNameError(undefined);
    }

    if (!formStatus.middleInitialValidity && middleInitialTouched) {
      setMiddleInitialError(t("enroll_form.enter_middle_initial"));
    } else {
      setMiddleInitialError(undefined);
    }

    if (!formStatus.lastNameValidity && lastNameTouched) {
      setLastNameError(t("enroll_form.enter_last_name"));
    } else {
      setLastNameError(undefined);
    }

    if (!formStatus.dobValidity && dobTouched) {
      setDobError(t("enroll_form.enter_dob"));
    } else {
      setDobError(undefined);
    }

    if (!formStatus.phoneValidity && phoneTouched) {
      setPhoneError(t("enroll_form.enter_phone_number"));
    } else {
      setPhoneError(undefined);
    }

    if (!formStatus.sexValidity && sexTouched) {
      setSexError(t("enroll_form.choose_sex"));
    } else {
      setSexError(undefined);
    }

    if (!formStatus.emailValidity) {
      setEmailError(t("enroll_form.enter_valid_email"));
    } else {
      setEmailError(undefined);
    }

    setValidForm(formStatus.isValid);
  };

  useEffect(() => {
    validateForm();
  }, [
    titleTouched,
    firstNameTouched,
    lastNameTouched,
    dobTouched,
    sexTouched,
    phoneTouched,
  ]);

  useEffect(() => {
    if (submitted && !validForm) {
      if (alertDiv.current) {
        alertDiv.current.scrollIntoView();
      }
    }
  }, [submitted]);

  const next = (): void => {
    setSubmitted(true);

    if (!validForm) {
      setDobTouched(true);
      setFirstNameTouched(true);
      setMiddleInitialTouched(true);
      setLastNameTouched(true);
      setTitleTouched(true);
      setSexTouched(true);
      setPhoneTouched(true);

      return;
    }

    nextPage();
  };

  useEffect(() => {
    validateForm();
  }, [enrollData]);

  return (
    <div>
      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enrollment_form.your_personal_info")}
      />

      <PlanInfo plan={plan} />

      <span className="sr-only">
        {t("sr_only_additional_questions_may_appear")}
      </span>

      <div ref={alertDiv}>
        {!validForm && submitted && (
          <Alert
            className="ds-u-margin-top--2"
            heading={t("enroll_form.needs_your_attention")}
            variation="error"
          >
            {t("enroll_form.complete_required")}
          </Alert>
        )}
      </div>

      <section>
        <Dropdown
          options={[
            {
              label: "",
              value: Title.NONE,
            },
            {
              label: t("enroll_form.mr"),
              value: Title.MR,
            },
            {
              label: t("enroll_form.mrs"),
              value: Title.MRS,
            },
            {
              label: t("enroll_form.ms"),
              value: Title.MS,
            },
            {
              label: t("enroll_form.miss"),
              value: Title.MISS,
            },
          ]}
          value={enrollData.title}
          errorMessage={titleError}
          label={t("enroll_form.title")}
          name="title"
          type="select"
          onBlur={(): void => setTitleTouched(true)}
          onChange={(e: ChangeEvent<HTMLSelectElement>): void => {
            setEnrollData({
              ...enrollData,
              title: e.currentTarget.value as Title,
            });
            setTitleTouched(true);
          }}
        />

        <TextField
          label={t("enroll_form.first_name")}
          onChange={(e: ChangeEvent<HTMLInputElement>): void => {
            setEnrollData({
              ...enrollData,
              firstName: e.target.value,
            });
            setFirstNameTouched(true);
          }}
          onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
            setEnrollData({
              ...enrollData,
              firstName: removeExtraWhiteSpace(e.target.value),
            });
            setFirstNameTouched(true);
          }}
          errorMessage={firstNameError}
          value={enrollData.firstName}
          name="first-name"
        />

        <TextField
          size="small"
          maxLength={1}
          label={`${t("enroll_form.middle_initial")} ${t(
            "enroll_form.optional"
          )}`}
          onChange={(e: ChangeEvent<HTMLInputElement>): void => {
            setEnrollData({ ...enrollData, middleInitial: e.target.value });
            setMiddleInitialTouched(true);
          }}
          errorMessage={middleInitialError}
          onBlur={(): void => setMiddleInitialTouched(true)}
          value={enrollData.middleInitial}
          name="middle-initial"
        />

        <TextField
          label={t("enroll_form.last_name")}
          errorMessage={lastNameError}
          onChange={(e: ChangeEvent<HTMLInputElement>): void => {
            setEnrollData({
              ...enrollData,
              lastName: e.target.value,
            });
            setLastNameTouched(true);
          }}
          onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
            setEnrollData({
              ...enrollData,
              lastName: removeExtraWhiteSpace(e.target.value),
            });
            setLastNameTouched(true);
          }}
          value={enrollData.lastName}
          name="last-name"
        />

        <DOBValidator
          hideAlert={true}
          label={t("csr_landing.dob")}
          onComplete={(date: MCTDate | boolean): void => {
            if (typeof date === "boolean") return;

            setEnrollData({ ...enrollData, dob: date });
            setDobTouched(
              !!date.day &&
                !!date.month &&
                !!date.year &&
                date.year.length === 4
            );
          }}
          defaultDate={enrollData.dob}
          errorMessage={dobError}
        />
      </section>

      <section>
        <PhoneNumberInput
          phoneNumber={enrollData.primaryPhone}
          name="primary-phone"
          errorMessage={phoneError}
          label={t("enroll_form.primary_phone")}
          onChange={(primaryPhone): void => {
            setEnrollData({ ...enrollData, primaryPhone });
            setPhoneTouched(
              primaryPhone.areaCode.length >= 1 &&
                primaryPhone.exchangeCode.length >= 1 &&
                primaryPhone.lineNumber.length === 4
            );
          }}
        />

        <TextField
          label={`${t("enroll_form.email")} ${t("enroll_form.optional")}`}
          name="email"
          type="email"
          errorMessage={emailError}
          value={enrollData.email}
          onChange={(e: ChangeEvent<HTMLInputElement>): void => {
            setEnrollData({
              ...enrollData,
              email: e.target.value,
            });
          }}
          onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
            setEnrollData({
              ...enrollData,
              email: removeExtraWhiteSpace(e.target.value),
            });
          }}
        />
      </section>

      <section>
        <ChoiceList
          type="radio"
          label={t("enroll_form.sex")}
          name="sex"
          errorMessage={sexError}
          choices={[
            {
              label: t("enroll_form.male"),
              value: Sex.MALE,
              checked: enrollData.sex === Sex.MALE,
            },
            {
              label: t("enroll_form.female"),
              value: Sex.FEMALE,
              checked: enrollData.sex === Sex.FEMALE,
            },
          ]}
          onChange={(e: ChangeEvent<HTMLInputElement>): void => {
            setEnrollData({
              ...enrollData,
              sex: e.target.value as Sex,
            });
          }}
        />

        <h2>
          {t("enroll_form.emergency_contact")} {t("enroll_form.optional")}
        </h2>

        <ChoiceList
          type="radio"
          label={t("enroll_form.provide_emergency_contact")}
          name="provide-emergency-contact"
          onChange={(e: ChangeEvent<HTMLInputElement>): void => {
            setEnrollData({
              ...enrollData,
              provideEmergencyContact: e.target.value === "Yes",
            });
          }}
          choices={[
            {
              label: t("yes"),
              value: "Yes",
              checked: enrollData.provideEmergencyContact,
            },
            {
              label: t("no"),
              value: "No",
              checked: enrollData.provideEmergencyContact === false,
            },
          ]}
        />

        {enrollData.provideEmergencyContact && (
          <>
            <TextField
              label={`${t("enroll_form.name")} ${t("enroll_form.optional")}`}
              value={enrollData.emergencyContactName}
              onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                setEnrollData({
                  ...enrollData,
                  emergencyContactName: e.target.value,
                })
              }
              onBlur={(e: ChangeEvent<HTMLInputElement>): void =>
                setEnrollData({
                  ...enrollData,
                  emergencyContactName: removeExtraWhiteSpace(e.target.value),
                })
              }
              name="emergency-name"
            />

            <PhoneNumberInput
              phoneNumber={enrollData.emergencyContactPhone}
              name="emergency-phone"
              label={`${t("enroll_form.phone_number")} ${t(
                "enroll_form.optional"
              )}`}
              onChange={(emergencyContactPhone): void =>
                setEnrollData({ ...enrollData, emergencyContactPhone })
              }
            />

            <TextField
              label={`${t("enroll_form.relationship")} ${t(
                "enroll_form.optional"
              )}`}
              name="emergency-relationship"
              value={enrollData.emergencyContactRelationship}
              onChange={(e: ChangeEvent<HTMLInputElement>): void =>
                setEnrollData({
                  ...enrollData,
                  emergencyContactRelationship: e.target.value,
                })
              }
              onBlur={(e: ChangeEvent<HTMLInputElement>): void =>
                setEnrollData({
                  ...enrollData,
                  emergencyContactRelationship: removeExtraWhiteSpace(
                    e.target.value
                  ),
                })
              }
            />
          </>
        )}
      </section>

      <BackNextButtons
        inReview={inReview}
        nextPage={next}
        previousPage={previousPage}
      />
    </div>
  );
};

export default PersonalInfo;
